/**
 * Locale-aware formatting utilities
 *
 * Use these functions instead of hardcoding locales like 'en-GB' throughout the codebase.
 * Locale is typically obtained from ConfigContext (config.locale?.localeCode).
 */

/** Input type accepted by all date formatters */
type DateInput = Date | string | null | undefined;

/**
 * Format a date value for display as dd/mm/yyyy
 *
 * @example
 * formatDate(new Date(), 'en-ZA') // '22/01/2026'
 * formatDate('2026-01-22') // '22/01/2026'
 */
export function formatDate(date: DateInput, _localeCode: string = 'en-GB'): string {
  if (!date) return '-';

  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return '-';

    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return '-';
  }
}

/**
 * Format a date and time value for display as dd/mm/yyyy, HH:MM
 *
 * @param timezone - Optional IANA timezone (e.g. 'Europe/Copenhagen'). When provided,
 *   hours/minutes are resolved in that timezone via Intl.DateTimeFormat. When omitted,
 *   browser-local time is used (backward-compatible).
 *
 * @example
 * formatDateTime(new Date(), 'en-ZA') // '22/01/2026, 14:30' (browser-local)
 * formatDateTime('2026-01-28T08:00:00Z', 'en-GB', 'Europe/Copenhagen') // '28/01/2026, 09:00'
 */
export function formatDateTime(date: DateInput, _localeCode: string = 'en-GB', timezone?: string): string {
  if (!date) return '-';

  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return '-';

    if (timezone) {
      const dtf = new Intl.DateTimeFormat('en-GB', {
        timeZone: timezone,
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      const parts = dtf.formatToParts(dateObj);
      const get = (type: string) => parts.find(p => p.type === type)?.value ?? '';
      return `${get('day')}/${get('month')}/${get('year')}, ${get('hour')}:${get('minute')}`;
    }

    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year}, ${hours}:${minutes}`;
  } catch {
    return '-';
  }
}

/**
 * Format a date as relative time (e.g., "2 hours ago", "yesterday")
 *
 * @example
 * formatRelativeTime(new Date(Date.now() - 3600000), 'en-GB') // '1 hour ago'
 */
export function formatRelativeTime(date: DateInput, localeCode: string = 'en-GB'): string {
  if (!date) return '-';

  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return '-';

    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    const rtf = new Intl.RelativeTimeFormat(localeCode, { numeric: 'auto' });

    if (diffSec < 60) return rtf.format(-diffSec, 'second');
    if (diffMin < 60) return rtf.format(-diffMin, 'minute');
    if (diffHour < 24) return rtf.format(-diffHour, 'hour');
    if (diffDay < 30) return rtf.format(-diffDay, 'day');

    // Fall back to full date for older dates
    return formatDate(dateObj, localeCode);
  } catch {
    return formatDate(date, localeCode);
  }
}

/**
 * Format a time-only value for display
 *
 * @example
 * formatTime(new Date(), 'en-GB') // '14:30'
 * formatTime(new Date(), 'en-US') // '2:30 PM'
 */
export function formatTime(date: DateInput, localeCode: string = 'en-GB'): string {
  if (!date) return '-';

  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return '-';

    return dateObj.toLocaleTimeString(localeCode, {
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return '-';
  }
}

// ---------------------------------------------------------------------------
// Named display formats — the canonical richer date shapes used across the
// portal. Each is the SAME `toLocaleDate/String('en-GB', { ... })` call it
// replaces, centralised and locale-pinned, so output is byte-identical and
// browser-local (no timezone is applied here — timezone-correct display uses
// the dedicated toLocalTime / toLocalDate / formatDateTime-with-timezone paths
// and the booking module's own formatLocalDate). Never inline a raw toLocale*
// date call; use one of these (see docs/guides/FRONTEND-CONSTITUTION.md).
// ---------------------------------------------------------------------------

/** Internal: date-only render with explicit options (browser-local, en-GB). */
function localeDate(date: DateInput, options: Intl.DateTimeFormatOptions): string {
  if (!date) return '-';
  try {
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-GB', options);
  } catch {
    return '-';
  }
}

/** Internal: date+time render with explicit options (browser-local, en-GB). */
function localeDateTime(date: DateInput, options: Intl.DateTimeFormatOptions): string {
  if (!date) return '-';
  try {
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleString('en-GB', options);
  } catch {
    return '-';
  }
}

/** "3 Jun 2026" — day, short month, year. */
export function formatDateMedium(date: DateInput): string {
  return localeDate(date, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "3 June 2026" — day, full month, year. */
export function formatDateLong(date: DateInput): string {
  return localeDate(date, { day: 'numeric', month: 'long', year: 'numeric' });
}

/** "3 Jun" — day and short month, no year. */
export function formatDayMonth(date: DateInput): string {
  return localeDate(date, { day: 'numeric', month: 'short' });
}

/** "Jun" — short month name only. */
export function formatMonthShort(date: DateInput): string {
  return localeDate(date, { month: 'short' });
}

/** "Jun 2026" (or "June 2026" with longMonth) — month and year. */
export function formatMonthYear(date: DateInput, longMonth: boolean = false): string {
  return localeDate(date, { month: longMonth ? 'long' : 'short', year: 'numeric' });
}

/** "Mon" — short weekday name only. */
export function formatWeekdayShort(date: DateInput): string {
  return localeDate(date, { weekday: 'short' });
}

/** "Mon 3 Jun" — short weekday, day, short month. */
export function formatWeekdayDayMonth(date: DateInput): string {
  return localeDate(date, { weekday: 'short', day: 'numeric', month: 'short' });
}

/** "Mon 3 Jun 2026" — short weekday, day, short month, year. */
export function formatWeekdayDayMonthYear(date: DateInput): string {
  return localeDate(date, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

/** "Monday 3 June" — full weekday, day, full month. */
export function formatWeekdayDateLong(date: DateInput): string {
  return localeDate(date, { weekday: 'long', day: 'numeric', month: 'long' });
}

/** "3 Jun 2026, 14:30" — medium date with time. */
export function formatDateTimeMedium(date: DateInput): string {
  return localeDateTime(date, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** "3 Jun, 14:30" — short date with time, no year. */
export function formatDateTimeShort(date: DateInput): string {
  return localeDateTime(date, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/**
 * Convert a UTC datetime to local time string in specified timezone
 *
 * @example
 * toLocalTime('2026-01-28T08:00:00+00', 'Europe/Copenhagen') // '10:00'
 * toLocalTime('2026-01-28T08:00:00+00', 'Europe/Copenhagen') // '09:00'
 */
export function toLocalTime(date: DateInput, timezone: string = 'UTC'): string {
  if (!date) return '';

  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return '';

    return dateObj.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: timezone
    });
  } catch {
    return '';
  }
}

/**
 * Convert a UTC datetime to local date string (YYYY-MM-DD) in specified timezone
 *
 * @example
 * toLocalDate('2026-01-28T22:00:00+00', 'Europe/Copenhagen') // '2026-01-29'
 */
export function toLocalDate(date: DateInput, timezone: string = 'UTC'): string {
  if (!date) return '';

  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return '';

    return dateObj.toLocaleDateString('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  } catch {
    return '';
  }
}

/**
 * Format a number according to locale
 *
 * @example
 * formatNumber(1234.56, 'en-GB') // '1,234.56'
 * formatNumber(1234.56, 'da-DK') // '1.234,56'
 */
export function formatNumber(value: number | null | undefined, localeCode: string = 'en-GB', options: Intl.NumberFormatOptions = {}): string {
  if (value === null || value === undefined) return '-';

  try {
    return new Intl.NumberFormat(localeCode, options).format(value);
  } catch {
    return String(value);
  }
}
